import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Typography, Box } from '@mui/material';
import { Save, Language } from '@mui/icons-material';

interface ConfigureDomainNameDialogProps {
	open: boolean;
	onClose: () => void;
	onSave: (domainName: string) => Promise<void>;
	currentDomainName?: string;
	environmentName: string;
}

export default function ConfigureDomainNameDialog({ open, onClose, onSave, currentDomainName, environmentName }: ConfigureDomainNameDialogProps) {
	const [domainName, setDomainName] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState('');

	// Initialize domain name when dialog opens or currentDomainName changes
	useEffect(() => {
		if (open) {
			setDomainName(currentDomainName || '');
			setError('');
		}
	}, [open, currentDomainName]);

	const validateDomainName = (value: string): boolean => {
		if (!value.trim()) {
			setError('Domain name is required');
			return false;
		}

		// Basic domain name validation
		// Allows: example.com, subdomain.example.com, localhost, 192.168.1.1, etc.
		const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
		const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

		if (!domainRegex.test(value.trim()) && !ipRegex.test(value.trim())) {
			setError('Please enter a valid domain name (e.g., example.com or subdomain.example.com)');
			return false;
		}

		setError('');
		return true;
	};

	const handleDomainNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setDomainName(value);

		// Clear error when user starts typing
		if (error) {
			setError('');
		}
	};

	const handleSave = async () => {
		if (!validateDomainName(domainName)) {
			return;
		}

		setIsSaving(true);
		try {
			await onSave(domainName.trim());
			onClose();
		} catch (err) {
			// Error handling is done by parent component
			console.error('Error saving domain name:', err);
		} finally {
			setIsSaving(false);
		}
	};

	const handleClose = () => {
		if (!isSaving) {
			onClose();
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !isSaving) {
			e.preventDefault();
			handleSave();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Language color="primary" />
					Configure Domain Name
				</Box>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<Typography variant="body2" color="textSecondary">
						Configure the domain name for the <strong>{environmentName}</strong> environment.
					</Typography>

					<TextField
						label="Domain Name"
						value={domainName}
						onChange={handleDomainNameChange}
						onKeyPress={handleKeyPress}
						fullWidth
						required
						autoFocus
						placeholder="e.g., example.com or app.example.com"
						helperText={error || 'Enter the domain name without protocol (http:// or https://)'}
						error={!!error}
						disabled={isSaving}
					/>

					{currentDomainName && currentDomainName !== domainName && (
						<Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
							<Typography variant="caption" color="info.main">
								Current domain: <strong>{currentDomainName}</strong>
							</Typography>
						</Box>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" startIcon={<Save />} disabled={isSaving || !domainName.trim()}>
					{isSaving ? 'Saving...' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
